import { useState } from "react";

interface Cliente {
  id: string;
  nombre: string;
  rut: string;
  categoria?: "socio" | "usuario";
  genero?: "" | "masculino" | "femenino";
  telefono: string;
  email: string;
  direccion: string;
  estado: string;
  medidor?: string;
}

interface Props {
  cliente: Cliente;
  onClose: () => void;
  onSave?: (updated: Cliente) => void;
}

export default function EditarClienteModal({ cliente, onClose, onSave }: Props) {
  const [form, setForm] = useState<Cliente>({
    ...cliente,
    categoria: cliente.categoria ?? "usuario",
    genero: cliente.genero ?? "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Cliente, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const validate = () => {
    const newErrors: Partial<Record<keyof Cliente, string>> = {};
    if (!form.nombre.trim()) newErrors.nombre = "El nombre es requerido";
    if (!form.direccion.trim()) newErrors.direccion = "La dirección es requerida";
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      onSave?.(form);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 1400);
    }, 900);
  };

  const field = (key: keyof Cliente) => ({
    value: (form[key] as string | undefined) ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setForm({ ...form, [key]: e.target.value });
      if (errors[key]) setErrors({ ...errors, [key]: undefined });
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl animate-[fadeIn_0.2s_ease] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20">
              <i className="ri-edit-line text-white text-sm" />
            </div>
            <div>
              <h2 className="font-semibold text-white text-sm">Editar Cliente</h2>
              <p className="text-teal-100 text-xs">{cliente.nombre}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Nombre Completo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...field("nombre")}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${
                errors.nombre ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-teal-400"
              }`}
              placeholder="Ej: María González Rojas"
            />
            {errors.nombre && <p className="text-xs text-red-500 mt-1">{errors.nombre}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">RUT</label>
              <input
                type="text"
                value={form.rut}
                readOnly
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">Modificar RUT desde "Actualizar RUT"</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Estado</label>
              <select
                {...field("estado")}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 cursor-pointer"
              >
                <option value="activo">Activo</option>
                <option value="moroso">Moroso</option>
                <option value="cortado">Cortado</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Categoría cliente</label>
              <select
                {...field("categoria")}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 cursor-pointer"
              >
                <option value="usuario">Usuario</option>
                <option value="socio">Socio</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Género</label>
              <select
                {...field("genero")}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 cursor-pointer"
              >
                <option value="">Sin definir</option>
                <option value="femenino">Femenino</option>
                <option value="masculino">Masculino</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Teléfono</label>
              <input
                type="text"
                {...field("telefono")}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="+56 9 XXXX XXXX"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Correo Electrónico</label>
              <input
                type="email"
                {...field("email")}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
                placeholder="correo@ejemplo.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Dirección del Servicio <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...field("direccion")}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none transition-colors ${
                errors.direccion ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-teal-400"
              }`}
              placeholder="Calle, número, sector"
            />
            {errors.direccion && <p className="text-xs text-red-500 mt-1">{errors.direccion}</p>}
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-information-line text-xs" />
              </div>
              <span className="text-xs text-gray-400">
                ID de cliente: <span className="font-mono font-medium text-gray-600">{cliente.id}</span> - no modificable
              </span>
            </div>
          </div>

          {saved && (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center text-emerald-600">
                <i className="ri-checkbox-circle-line" />
              </div>
              <span className="text-sm text-emerald-700 font-medium">Cambios guardados correctamente</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || saved}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </div>
                  Guardando...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-save-line" />
                  </div>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
